import { zodResolver } from '@hookform/resolvers/zod'
import { Box, Grid, InputAdornment, Typography } from '@mui/material'
import {
  LocalizationProvider,
  MobileDatePicker,
  MobileTimePicker,
} from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { IconCalendarEvent, IconClock } from '@tabler/icons-react'
import dayjs from 'dayjs'
import React, { useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { z } from 'zod'
import PlainModal from '../../../../components/modal/PlainModal'
import { FilterRangeSlider } from '../../../../components/styled/filter/FilterList.styled'
import InputComponent, {
  ResactlyInputLabel,
} from '../../../../components/ui/InputComponent'
import { ROUTES } from '../../../../helpers/routes-helper'
import { DATE_FORMAT } from '../../../../helpers/static-constants'
import useAppUtilities from '../../../../hooks/useAppUtilities'
import { useAppSelector } from '../../../../store'
import { setReservation } from '../../../../store/reservation'
import { selectRestaurantDetail } from '../../../../store/restaurantSlice'
import { ReservationStatus } from '../../../../types/reservationTypes'
import ReservationSuccessfulDialog from './ReservationSuccessfulDialog'

interface MakeReservationDialogProps {
  open: boolean
  handleClose: () => void
}

const schema = z.object({
  date: z.date({ required_error: 'Date is required' }).nullable(),
  time: z.date({ required_error: 'Time is required' }).nullable(),
  guests: z.number().min(2, 'At least 2 guests').max(20, 'Maximum 20 guests'),
})

type FormValues = z.infer<typeof schema>

const MakeReservationDialog: React.FC<MakeReservationDialogProps> = ({
  open,
  handleClose,
}) => {
  const restaurant = useAppSelector(selectRestaurantDetail)?.restaurantDTO
  const { dispatch, navigate } = useAppUtilities()
  const [reservationSuccessOpen, setReservationSuccessOpen] =
    useState<boolean>(false)
  const [bookTime, setBookTime] = useState<string>('')

  const {
    handleSubmit,
    control,
    reset,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      date: dayjs().toDate(),
      time: dayjs().add(1, 'hour').toDate(),
      guests: 2,
    },
  })

  const onHandleLoadMenu = async (): Promise<void> => {
    await handleSubmit(async (data: z.infer<typeof schema>): Promise<void> => {
      if (restaurant) {
        dispatch(
          setReservation({
            id: `${dayjs(data.date).format(DATE_FORMAT)}-${dayjs(
              data.time,
            ).format('h-mm-ss')}-${restaurant.name}`,
            status: ReservationStatus.UPCOMING,
            reservationDate: dayjs(data.date).format(DATE_FORMAT),
            time: dayjs(data.time).format('h:mm A'),
            guests: data.guests,
            restaurant,
          }),
        )
        setBookTime(
          `${dayjs(data.date).format('D MMMM YYYY')}, ${dayjs(data.time).format(
            'h:mm A',
          )}`,
        )
        setReservationSuccessOpen(true)
        onCancel()
      }
    })()
  }

  const onCancel = (): void => {
    reset()
    handleClose()
  }

  const onHandleReservationsView = (): void => {
    setReservationSuccessOpen(false)
    navigate(ROUTES.RESERVATIONS)
  }
  return (
    <>
      <PlainModal
        open={open}
        handleClose={handleClose}
        modelWidth="500px"
        title="Make Reservation"
        actionButtonName="Book"
        cancelButtonName="Cancel"
        onAction={() => {
          void onHandleLoadMenu()
        }}
        onCancel={onCancel}
      >
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <ResactlyInputLabel
                label="Date"
                htmlFor="date"
                isFieldRequired
                labelFontSize="14px"
                isValid={!!errors.date}
              />
              <Controller
                name="date"
                control={control}
                render={({ field, fieldState }) => (
                  <MobileDatePicker
                    {...field}
                    value={field.value ? dayjs(field.value) : null}
                    onChange={(newValue) => {
                      field.onChange(newValue?.toDate())
                    }}
                    format="DD-MM-YYYY"
                    minDate={dayjs()}
                    defaultValue={dayjs()}
                    slotProps={{
                      textField: {
                        id: 'date',
                        fullWidth: true,
                        error: !!fieldState.error,
                        helperText: fieldState.error?.message ?? ' ',
                        InputProps: {
                          endAdornment: (
                            <InputAdornment position="start">
                              <IconCalendarEvent stroke={2} />
                            </InputAdornment>
                          ),
                        },
                      },
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        '& fieldset': {
                          borderRadius: '8px',
                          borderColor: 'grey.300',
                          borderWidth: '1px',
                        },
                        '&:hover fieldset': {
                          borderColor: 'grey.300',
                          borderWidth: '1px',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: 'grey.300',
                          borderWidth: '1px',
                        },
                      },
                      '& .MuiInputBase-root': {
                        borderRadius: '8px',
                        pr: '0px !important',
                      },
                      '& .MuiInputBase-input': {
                        padding: '10px 10px',
                        fontSize: '14px',
                        lineHeight: '20px',
                        fontWeight: 400,
                      },
                    }}
                  />
                )}
              />
            </Grid>

            <Grid item xs={6}>
              <ResactlyInputLabel
                label="Time"
                htmlFor="time"
                isFieldRequired
                labelFontSize="14px"
                isValid={!!errors.date}
              />
              <Controller
                name="time"
                control={control}
                render={({ field, fieldState }) => (
                  <MobileTimePicker
                    {...field}
                    value={field.value ? dayjs(field.value) : null}
                    onChange={(newValue) => {
                      field.onChange(newValue?.toDate())
                    }}
                    minTime={dayjs().add(1, 'hour')}
                    slotProps={{
                      textField: {
                        id: 'time',
                        fullWidth: true,
                        error: !!fieldState.error,
                        helperText: fieldState.error?.message ?? ' ',
                        InputProps: {
                          endAdornment: (
                            <InputAdornment position="start">
                              <IconClock stroke={2} />
                            </InputAdornment>
                          ),
                        },
                      },
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        '& fieldset': {
                          borderRadius: '8px',
                          borderColor: 'grey.300',
                          borderWidth: '1px',
                        },
                        '&:hover fieldset': {
                          borderColor: 'grey.300',
                          borderWidth: '1px',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: 'grey.300',
                          borderWidth: '1px',
                        },
                      },
                      '& .MuiInputBase-root': {
                        borderRadius: '8px',
                        pr: '0px !important',
                      },
                      '& .MuiInputBase-input': {
                        padding: '10px 10px',
                        fontSize: '14px',
                        lineHeight: '20px',
                        fontWeight: 400,
                      },
                    }}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12}>
              <Controller
                name="guests"
                control={control}
                render={({ field }) => (
                  <>
                    <InputComponent
                      label="Number of guests "
                      id="guests"
                      {...field}
                      value={field.value || 2}
                      type="number"
                      sx={{
                        mt: '6px',
                      }}
                      fullWidth
                    />

                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexDirection: 'column',
                        mt: 1.5,
                        mb: 1,
                      }}
                    >
                      <FilterRangeSlider
                        getAriaLabel={() => 'Minimum distance shift'}
                        {...field}
                        value={field.value || 2}
                        disableSwap
                        min={2}
                        max={20}
                        marks={[
                          { value: 2, label: '2' },
                          { value: 4, label: '4' },
                          { value: 6, label: '6' },
                          { value: 8, label: '8' },
                          { value: 10, label: '10' },
                          { value: 12, label: '12' },
                          { value: 14, label: '14' },
                          { value: 16, label: '16' },
                          { value: 18, label: '18' },
                          { value: 20, label: '20+' },
                        ]}
                      />
                      {errors.guests && (
                        <Typography color="error" variant="caption">
                          {errors.guests.message}
                        </Typography>
                      )}
                    </Box>
                  </>
                )}
              />
            </Grid>
          </Grid>
        </LocalizationProvider>
      </PlainModal>
      <ReservationSuccessfulDialog
        open={reservationSuccessOpen}
        handleClose={() => setReservationSuccessOpen(false)}
        actionButton={onHandleReservationsView}
        actionBUttonText="Take me to My Reservations"
        subTitle="Lorem ipsum dolor sit amet consectetur. Et tellus interdum nunc malesuada."
        title="Reservation Successful"
        time={bookTime}
      />
    </>
  )
}

export default MakeReservationDialog
